import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';
import {
  SurveyContentsHeader,
  BoldText,
  QuestionsInfo,
  BodyCont,
  BodyTitle,
  ContentBody,
  StaticContainerScreen,
  SurveyContentsBody,
  SpinnerCont,
  SurveyContentsCont,
  HeadTitle,
  Header,
} from './SurveyContents.styles';
import { ConfirmSurveyQuitModal } from '../../components/ConfirmSurveyQuitModal/ConfirmSurveyQuitModal';
import SearchDropdown, {
  ISearchDropdownItem,
} from '../../components/SearchDropdown/SearchDropdown';
import Spinner from '../../components/Spinner/Spinner';
import SurveyAccordion from '../../components/SurveyAccordion/SurveyAccordion';
import { CloseOpenIconCont } from '../../components/SurveyAccordion/SurveyAccordion.styles';
import OpenQuestions from './OpenQuestions/OpenQuestions';
import { formatSurveyTypeLabel, surveyTypes } from '../../constans/forms';
import { useAccordion } from '../../hooks/useAccordion';
import { useFillViewportHeight } from '../../hooks/useFillViewportHeight';
import { useScroll } from '../../hooks/useScroll';
import { IGenerateContentsArray, IStaticData } from '../../hooks/useSurveyContents';
import { ITopicbaseOpenQuestion } from '../../services/topicbase';
import { IGetSurveyOverviewResp, ISurveyContentsItem, ISurveyScope } from '../../services/surveys';
import { BodyRegularS } from '../../styles/globals';
import CloseIcon from '@/assets/icons/closeAllAccordion.svg?react';
import OpenIcon from '@/assets/icons/openAllAccordion.svg?react';

interface ISurveyContents {
  isIntersecting: boolean;
  surveyOverview: IGetSurveyOverviewResp;
  onSetSurveyData: (introOutro: ISurveyContents['surveyContentsData']['hellAndEndInitial']) => void;
  onSetSurveyScope: (scope: ISurveyScope[]) => void;
  onSetOpenQuestions: (questions: { code: string }[]) => void;
  setIsTransitionBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  isTransitionBlocked: boolean;
  onConfirmQuit: () => void;
  surveyContentsData: {
    generateContentsArray: IGenerateContentsArray[];
    language: string;
    staticData: IStaticData;
    loading: boolean;
    hellAndEndInitial: IStaticData & {
      title: {
        nb: string;
        en: string;
        sv: string;
      };
    };
    openQuestions: { code: string }[] | undefined;
    topicbaseOpenQuestions: ITopicbaseOpenQuestion[];
  };
}

const SurveyContents: FC<ISurveyContents> = ({
  isIntersecting,
  surveyOverview,
  onSetSurveyData,
  onSetSurveyScope,
  onSetOpenQuestions,
  setIsTransitionBlocked,
  isTransitionBlocked,
  surveyContentsData,
  onConfirmQuit,
}) => {
  const { type } = surveyOverview;
  const isEffect = ['EFFECT', 'EFFECT_EXTERNAL'].includes(type);
  // Gode Team surveys always cover the full topicbase, so the scope can't be edited
  const isScopeLocked = type === surveyTypes.GOOD_TEAM;

  const blocker = useBlocker(isTransitionBlocked);
  const {
    generateContentsArray,
    language,
    staticData,
    loading,
    hellAndEndInitial,
    openQuestions: initialOpenQuestions,
    topicbaseOpenQuestions,
  } = surveyContentsData;

  const { scrollToId } = useScroll();

  const { assignedItemsArr, notAssignedItemsArr, changeItemAssigment, changeItemsAssigment } =
    useAccordion(generateContentsArray);
  const { t } = useTranslation();

  const surveyContentRef = useRef<HTMLDivElement>(null);
  const bodyHeight = useFillViewportHeight(surveyContentRef, !loading);
  const [isExpanded, setIsExpanded] = useState({
    helloScreen: true,
    endScreen: true,
    helpToggleScreen: true,
  });
  const [scope, setScope] = useState<{
    [k: string]: { code: string; items: ISurveyContentsItem[] }[];
  }>({});
  const [revealedCategories, setRevealedCategories] = useState<{
    [k: string]: boolean;
  }>({});

  const [activeOpenQuestions, setActiveOpenQuestions] = useState<{ code: string }[]>([]);

  const timeoutId = useRef<number | undefined>(undefined);

  const { introText, outroText, helpText } = staticData ?? {};

  const surveyScope = useMemo(
    () =>
      Object.entries(scope)
        .filter(([_, topics]) => topics.length > 0)
        .map(([key, topics]) => ({
          code: key,
          topics: topics.map(({ code, items }) => ({
            code,
            items: items.map(({ code }) => code),
          })),
        })),
    [scope]
  );

  const estimatedTime = Math.ceil(
    Object.values(scope).reduce(
      (acc, topics) =>
        acc +
        topics.reduce(
          (acc, { items }) =>
            acc +
            items.reduce((acc, { estimatedResponseTime }) => acc + estimatedResponseTime / 60, 0),
          0
        ),
      0
    )
  );

  const questionsCount = Object.values(scope).reduce(
    (acc, topics) => acc + topics.reduce((acc, { items }) => acc + items.length, 0),
    0
  );

  const handleSetRevealedCategories = useCallback(
    (code: string, isCategoryRevealed: boolean) => {
      setRevealedCategories(prevState => ({
        ...prevState,
        [code]: isCategoryRevealed,
      }));
    },
    [setRevealedCategories]
  );

  const handleChangeCategoryAssignment = useCallback(
    (id: string) => {
      changeItemAssigment(id);
    },
    [changeItemAssigment]
  );

  const handleSetScope = (
    code: string,
    scope: { code: string; items: ISurveyContentsItem[] }[]
  ) => {
    setScope(prevState => ({ ...prevState, [code]: scope }));
  };

  const handleCancelQuit = () => {
    blocker.reset?.();
  };

  const handleConfirmQuit = () => {
    setIsTransitionBlocked(false);
    onConfirmQuit();
    blocker.proceed?.();
  };

  const handleRemoveOpenQuestion = useCallback(
    (code: string) => {
      setActiveOpenQuestions(prev => prev.filter(item => item.code !== code));
      setIsTransitionBlocked(true);
    },
    [setIsTransitionBlocked]
  );

  const handleAddOpenQuestion = useCallback(
    (code: string) => {
      setActiveOpenQuestions(prev => [...prev, { code }]);
      setIsTransitionBlocked(true);
    },
    [setIsTransitionBlocked]
  );

  const handleSelectCategories = (elements: ISearchDropdownItem[]) => {
    changeItemsAssigment(elements.map(item => item.id));
    setIsTransitionBlocked(true);

    if (timeoutId?.current) {
      window.clearTimeout(timeoutId.current);
    }

    const firstSelectedName = elements[0]?.name;
    const scrollTarget = typeof firstSelectedName === 'string' ? firstSelectedName : '';

    timeoutId.current = window.setTimeout(() => {
      scrollToId(scrollTarget);
    }, 250);
  };

  const renderAccordions = useMemo(() => {
    return (assignedItemsArr.length > 0 ? assignedItemsArr : notAssignedItemsArr).map(
      (item: IGenerateContentsArray) => (
        <SurveyAccordion
          key={item.id}
          language={language}
          title={item.name}
          insertedCategoryId={null}
          item={item}
          revealedCategories={revealedCategories}
          isEffect={isEffect}
          isScopeLocked={isScopeLocked}
          onChangeCategoryAssignment={handleChangeCategoryAssignment}
          onSetScope={handleSetScope}
          onSetRevealedCategories={handleSetRevealedCategories}
          setIsTransitionBlocked={setIsTransitionBlocked}
          changeCategoryAssigment={id => handleSelectCategories([{ id, name: item.name }])}
        />
      )
    );
  }, [assignedItemsArr, revealedCategories, setIsTransitionBlocked, isEffect, isScopeLocked]);

  const dropdownItems = notAssignedItemsArr.map(({ name, id }) => ({
    name,
    id,
    shortDisplayName: name,
  }));

  useEffect(() => {
    if (initialOpenQuestions) {
      setActiveOpenQuestions(initialOpenQuestions);
    }
  }, [initialOpenQuestions]);

  useEffect(() => {
    onSetOpenQuestions(activeOpenQuestions);
  }, [activeOpenQuestions, onSetOpenQuestions]);

  useEffect(() => {
    onSetSurveyScope(surveyScope);
  }, [surveyScope, onSetSurveyScope]);

  useEffect(() => {
    if (introText || outroText || helpText) {
      onSetSurveyData(hellAndEndInitial);
    }
  }, [introText, helpText, hellAndEndInitial, onSetSurveyData]);

  useEffect(() => {
    if (blocker.state === 'blocked' && !isTransitionBlocked) {
      blocker.reset();
    }
  }, [blocker, setIsTransitionBlocked, isTransitionBlocked]);

  return (
    <>
      {loading ? (
        <SpinnerCont id="SpinnerCont" data-testid="spinnerContentsTab">
          <Spinner size="large" />
        </SpinnerCont>
      ) : null}
      <SurveyContentsCont loading={loading}>
        <SurveyContentsHeader
          data-testid="contentsTabHeader"
          id="SurveyContentsHeader"
          isIntersecting={isIntersecting}
        >
          <span>
            {t('viewSurvey.contents.basedOn')}:{' '}
            <BoldText>{formatSurveyTypeLabel(type)} survey</BoldText>
          </span>
          <QuestionsInfo id="QuestionsInfo">
            {`${t('viewSurvey.contents.questions')}: `}
            <BoldText id="BoldText">{questionsCount}</BoldText>
          </QuestionsInfo>
          <span>
            {`${t('viewSurvey.contents.estimateResponseTime')}: `}
            <BoldText id="BoldText">
              {`${t('viewSurvey.contents.upTo')} ${estimatedTime} ${t('viewSurvey.contents.min')}`}
            </BoldText>
          </span>
        </SurveyContentsHeader>
        <SurveyContentsBody>
          <ContentBody>
            <Header>
              <BodyTitle id="surveyContents">{t('viewSurvey.body.surveyContents')}</BodyTitle>
              {isEffect || isScopeLocked ? null : (
                <SearchDropdown
                  width="320px"
                  items={dropdownItems}
                  placeholder={t('viewSurvey.contents.dropdown.placeholder')}
                  noResultsMessage={t('viewSurvey.contents.dropdown.noResultsMessage')}
                  selectElements={handleSelectCategories}
                  selectFullObject
                  isMulti
                />
              )}
            </Header>

            <BodyCont isInViewPort={isIntersecting} $fillHeight={bodyHeight} ref={surveyContentRef}>
              {introText && (
                <StaticContainerScreen id={t('viewSurvey.body.helloScreen', { lng: language })}>
                  <HeadTitle id="HelloScreenTitle">
                    {t('viewSurvey.body.helloScreen', { lng: language })}
                  </HeadTitle>

                  <CloseOpenIconCont
                    data-testid="closeOpenHelloScreenBtn"
                    onClick={() =>
                      setIsExpanded(prev => ({
                        ...prev,
                        helloScreen: !prev.helloScreen,
                      }))
                    }
                  >
                    {isExpanded.helloScreen ? <CloseIcon /> : <OpenIcon />}
                  </CloseOpenIconCont>
                  {isExpanded.helloScreen ? (
                    <BodyRegularS data-testid="helloScreenDetails">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: staticData?.introText || '',
                        }}
                      />
                    </BodyRegularS>
                  ) : null}
                </StaticContainerScreen>
              )}

              {helpText && (
                <StaticContainerScreen id={t('viewSurvey.body.helpScreen', { lng: language })}>
                  <HeadTitle id="HelpScreenTitle">
                    {t('viewSurvey.body.helpScreen', { lng: language })}
                  </HeadTitle>
                  <CloseOpenIconCont
                    data-testid="closeOpenHelpScreenBtn"
                    onClick={() =>
                      setIsExpanded(prev => ({
                        ...prev,
                        helpToggleScreen: !prev.helpToggleScreen,
                      }))
                    }
                  >
                    {isExpanded.helpToggleScreen ? <CloseIcon /> : <OpenIcon />}
                  </CloseOpenIconCont>
                  {isExpanded.helpToggleScreen ? (
                    <BodyRegularS data-testid="helpScreenDetails">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: staticData?.helpText || '',
                        }}
                      />
                    </BodyRegularS>
                  ) : null}
                </StaticContainerScreen>
              )}

              {renderAccordions}

              {isEffect && topicbaseOpenQuestions.length > 0 && (
                <OpenQuestions
                  topicbaseOpenQuestions={topicbaseOpenQuestions}
                  activeOpenQuestions={activeOpenQuestions}
                  language={language}
                  onAdd={handleAddOpenQuestion}
                  onRemove={handleRemoveOpenQuestion}
                />
              )}

              <StaticContainerScreen id={t('viewSurvey.body.endScreen', { lng: language })}>
                <HeadTitle>{t('viewSurvey.body.endScreen', { lng: language })}</HeadTitle>
                <CloseOpenIconCont
                  data-testid="closeOpenEndScreenBtn"
                  onClick={() =>
                    setIsExpanded(prev => ({
                      ...prev,
                      endScreen: !prev.endScreen,
                    }))
                  }
                >
                  {isExpanded.endScreen ? <CloseIcon /> : <OpenIcon />}
                </CloseOpenIconCont>
                {/*<PrologEpilogTitle>*/}
                {/*Intro information of the survey*/}
                {/*</PrologEpilogTitle>*/}
                {/*<p />*/}
                {isExpanded.endScreen ? (
                  <BodyRegularS data-testid="endScreenDetails">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: staticData?.outroText || '',
                      }}
                    />
                  </BodyRegularS>
                ) : null}
              </StaticContainerScreen>
            </BodyCont>
          </ContentBody>
        </SurveyContentsBody>
      </SurveyContentsCont>
      <ConfirmSurveyQuitModal
        onClose={handleCancelQuit}
        onConfirm={handleConfirmQuit}
        active={blocker.state === 'blocked'}
      />
    </>
  );
};

export default SurveyContents;
